import memoize from '@github/memoize';
import { function as F, io, readonlyNonEmptyArray as RNEA } from 'fp-ts';
import * as mobx from 'mobx';
import * as mobxUtils from 'mobx-utils';
import {
  from as fromStream,
  MonoTypeOperatorFunction,
  Observable,
  OperatorFunction,
} from 'rxjs';
import * as rx from 'rxjs/operators';

/**
 * Wrap mobx.makeObservable with mobx.runInAction.
 * This will help to avoid issues when observable fields have default value.
 */
export const makeObservable: typeof mobx.makeObservable = (
  target,
  annotations,
  options,
) => mobx.runInAction(() => mobx.makeObservable(target, annotations, options));

interface ToIO<T> {
  <K extends keyof T>(key: K): io.IO<T[K]>;
  <KS extends RNEA.ReadonlyNonEmptyArray<keyof T>>(keys: KS): io.IO<{
    readonly [key in KS[number]]: T[key];
  }>;
  <KS extends RNEA.ReadonlyNonEmptyArray<keyof T>>(...keys: KS): io.IO<{
    readonly [key in KS[number]]: T[key];
  }>;
}

const fromKeyToIO =
  <T>(store: T) =>
  <K extends keyof T>(key: K): io.IO<T[K]> =>
  () =>
    store[key];

const fromKeysToIO =
  <T>(store: T) =>
  <KS extends RNEA.ReadonlyNonEmptyArray<keyof T>>(
    keys: KS,
  ): io.IO<{ readonly [key in KS[number]]: T[key] }> =>
  () =>
    F.pipe(
      keys,
      RNEA.reduce({} as { [key in KS[number]]: T[key] }, (acc, key) => {
        acc[key] = store[key];

        return acc;
      }),
    );

/**
 * Converts mobx observable field(s) to io.IO
 */
export const toIO: <T extends object>(store: T) => ToIO<T> = memoize(
  (store): ToIO<typeof store> =>
    memoize((...args) => {
      if (args.length === 1) {
        if (Array.isArray(args[0])) {
          return fromKeysToIO(store)(
            args[0] as unknown as RNEA.ReadonlyNonEmptyArray<keyof typeof store>,
          );
        }

        return fromKeyToIO(store)(args[0] as keyof typeof store);
      }

      return fromKeysToIO(store)(
        args as unknown as RNEA.ReadonlyNonEmptyArray<keyof typeof store>,
      );
    }) as ToIO<typeof store>,
  {
    hash: store => store,
    // @ts-expect-error
    cache: new WeakMap(),
  },
);

/**
 * Converts mobx observable field to rxjs's Observable.
 */
export const toRXWithOptions: <
  T extends { readonly takeUntilDestroy: <X>() => MonoTypeOperatorFunction<X> },
>(
  store: T,
) => (opts?: {
  fireImmediately?: boolean;
  distinctUntilChanged?: boolean;
  toJS?: boolean;
  shareReplay?: boolean;
}) => <K extends keyof T>(key: K) => Observable<T[K]> = memoize(
  store =>
    memoize(
      (
        {
          fireImmediately = true,
          distinctUntilChanged = false,
          toJS = true,
          shareReplay = true,
        }: {
          fireImmediately?: boolean;
          distinctUntilChanged?: boolean;
          toJS?: boolean;
          shareReplay?: boolean;
        } = {
          fireImmediately: true,
          distinctUntilChanged: false,
          toJS: true,
          shareReplay: true,
        },
      ) =>
        memoize(key => {
          const pipes: OperatorFunction<
            typeof store[typeof key],
            typeof store[typeof key]
          >[] = [];

          if (toJS) {
            pipes.push(rx.map(x => mobx.toJS(x)));
          }

          if (distinctUntilChanged) {
            pipes.push(rx.distinctUntilChanged());
          }

          if (shareReplay) {
            pipes.push(store.takeUntilDestroy());
            pipes.push(rx.shareReplay());
          }

          pipes.push(store.takeUntilDestroy());

          return fromStream(mobxUtils.toStream(toIO(store)(key), fireImmediately) as unknown as Observable<typeof store[typeof key]>).pipe(
            ...(pipes as [
              OperatorFunction<typeof store[typeof key], typeof store[typeof key]>,
            ]),
          );
        }),
    ),
  {
    hash: store => store,
    // @ts-expect-error
    cache: new WeakMap(),
  },
);
